import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { PlusIcon, CalendarIcon } from '../components/NavIcons';
import { useToast, PageLoader } from '../../design-system';
import { SHOW_DASHBOARD_LOADER } from '../../config/loaders';
import '../styles/OrgAdminDashboard.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

// Four buckets summarised from Task.status (Pending / Hold / In-Progress /
// Completed). `review` isn't surfaced here. Warm palette matches the reference.
const DASH_STATUS = [
  { key: 'todo', label: 'Pending', color: '#B08428' },
  { key: 'waiting-client', label: 'Waiting', color: '#C9A227' },
  { key: 'on-hold', label: 'Hold', color: '#D8D5D0' },
  { key: 'in-progress', label: 'In Progress', color: '#2B3A4A' },
  { key: 'completed', label: 'Completed', color: '#4F6F5C' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Warm accent per deadline card (by position) and per attention counter.
const DL_TONE = ['c-deep', 'c-deep', 'c-sage', 'c-gold', 'c-clay'];
const ATTN_TONE = { approvals: 'c-gold', overdueComp: 'c-clay', notices: 'c-deep', renewals: 'c-deep' };

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const KebabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="5.5" r="1.4" fill="currentColor" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    <circle cx="12" cy="18.5" r="1.4" fill="currentColor" />
  </svg>
);

const ChevronDown = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function OrgAdminDashboard() {
  const revision = useLiveRefresh();
  const [tasks, setTasks] = useState([]);
  const [todos, setTodos] = useState([]);
  const [activeTaskTab, setActiveTaskTab] = useState('todo');
  // Operational load for a compliance firm — statutory deadlines, the approval
  // backlog, renewals and notices. These are the things an admin needs on the
  // dashboard but the task/to-do view alone doesn't show.
  const [ops, setOps] = useState({
    overdueCompliance: [],
    upcomingCompliance: [],
    proposed: 0,
    renewals: 0,
    notices: { open: 0, overdue: 0, dueSoon: 0 },
  });
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      await Promise.allSettled([fetchTasks(), fetchTodos(), fetchOps()]);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const fetchTasks = async () => {
    try {
      const res = await apiClient.get('/api/tasks');
      setTasks(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load tasks');
    }
  };

  // Admin dashboard: the whole org's to-dos as numbers — no owner scoping and no
  // item list. Drilling into the actual rows is what the To-Do page is for.
  const fetchTodos = async () => {
    try {
      const res = await apiClient.get('/api/todos');
      setTodos(res.data);
    } catch (err) {
      toast.danger('Failed to load to-dos');
    }
  };

  // Statutory calendar + backlog. One instances call spans [-1yr, +45d] of DATED
  // rows, split client-side into overdue vs upcoming (open statuses only). Each
  // source is settled on its own so an empty/failed one never blanks the rest.
  const fetchOps = async () => {
    const from = new Date(Date.now() - 365 * 864e5).toISOString();
    const to = new Date(Date.now() + 45 * 864e5).toISOString();
    const [instRes, propRes, renRes, notRes] = await Promise.allSettled([
      apiClient.get('/api/instances', { params: { from, to } }),
      apiClient.get('/api/tasks', { params: { approvalStatus: 'proposed' } }),
      apiClient.get('/api/expirables/expiring-soon', { params: { days: 30 } }),
      apiClient.get('/api/notices/summary'),
    ]);
    const instances = instRes.status === 'fulfilled' ? instRes.value.data : [];
    const todayMs2 = startOfDay(new Date()).getTime();
    const openDated = instances.filter(
      (i) => ['upcoming', 'in_progress'].includes(i.status) && i.statutoryDueDate
    );
    setOps({
      overdueCompliance: openDated.filter((i) => startOfDay(new Date(i.statutoryDueDate)).getTime() < todayMs2),
      upcomingCompliance: openDated.filter((i) => startOfDay(new Date(i.statutoryDueDate)).getTime() >= todayMs2),
      proposed: propRes.status === 'fulfilled' ? propRes.value.data.length : 0,
      renewals: renRes.status === 'fulfilled' ? renRes.value.data.length : 0,
      notices: notRes.status === 'fulfilled' ? notRes.value.data : { open: 0, overdue: 0, dueSoon: 0 },
    });
  };

  const today = startOfDay(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const in7Days = new Date(today);
  in7Days.setDate(today.getDate() + 7);
  const overdueBoundary = new Date(today);
  overdueBoundary.setDate(today.getDate() - 7);

  const openTasksWithDueDate = tasks.filter((t) => t.status !== 'completed' && t.dueDate);

  // ⚠️ `due` IS THE TASK LIST'S OWN FILTER VALUE, and each one is defined
  // server-side to match the bucket beside it exactly (taskController#
  // applyDueFilter). The tiles are clickable, so the number shown here and the
  // number of rows the click opens have to agree — pointing the two overdue
  // tiles at the broad `overdue` filter would open every overdue task from
  // either of them, and a count that changes when you click it is a count
  // nobody trusts again.
  const dueBuckets = [
    { key: 'today', due: 'today', main: 'Due', sub: 'Today', count: openTasksWithDueDate.filter((t) => startOfDay(new Date(t.dueDate)).getTime() === today.getTime()).length },
    { key: 'tomorrow', due: 'tomorrow', main: 'Due', sub: 'Tomorrow', count: openTasksWithDueDate.filter((t) => startOfDay(new Date(t.dueDate)).getTime() === tomorrow.getTime()).length },
    { key: 'next7', due: 'next7', main: 'Due', sub: 'In 7 days', count: openTasksWithDueDate.filter((t) => { const d = startOfDay(new Date(t.dueDate)); return d > tomorrow && d <= in7Days; }).length },
    { key: 'overdue7', due: 'overdue_recent', main: 'Overdue', sub: 'Up to 7 days', count: openTasksWithDueDate.filter((t) => { const d = startOfDay(new Date(t.dueDate)); return d < today && d >= overdueBoundary; }).length },
    { key: 'overdueBeyond', due: 'overdue_old', main: 'Overdue', sub: 'More than 7 days', count: openTasksWithDueDate.filter((t) => startOfDay(new Date(t.dueDate)) < overdueBoundary).length },
  ];

  const statusCounts = DASH_STATUS.map((s) => ({ ...s, value: tasks.filter((t) => t.status === s.key).length }));
  const summaryTotal = statusCounts.reduce((sum, s) => sum + s.value, 0);
  const completedCount = statusCounts.find((s) => s.key === 'completed')?.value || 0;
  const needToComplete = summaryTotal - completedCount;

  const taskTabs = DASH_STATUS.map((s) => ({ key: s.key, label: s.label }));

  // Rows grouped by client; bar length = count relative to the busiest group.
  const groupedRows = useMemo(() => {
    const groups = new Map();
    tasks.forEach((t) => {
      if (t.status !== activeTaskTab) return;
      const name = t.clientId?.displayName || 'Internal / No client';
      groups.set(name, (groups.get(name) || 0) + 1);
    });
    return Array.from(groups.entries())
      .map(([name, active]) => ({ name, active }))
      .sort((a, b) => b.active - a.active)
      .slice(0, 12);
  }, [tasks, activeTaskTab]);

  const maxActive = Math.max(1, ...groupedRows.map((g) => g.active));

  // To-do counts across the org. "Open" includes UNDATED to-dos; overdue is
  // called out separately.
  const todayMs = today.getTime();
  const todoStats = useMemo(() => {
    const open = todos.filter((t) => t.status !== 'completed');
    const dueOn = (t) => (t.dueDate ? startOfDay(new Date(t.dueDate)).getTime() : null);
    return {
      open: open.length,
      overdue: open.filter((t) => dueOn(t) != null && dueOn(t) < todayMs).length,
      dueToday: open.filter((t) => dueOn(t) === todayMs).length,
      upcoming: open.filter((t) => dueOn(t) != null && dueOn(t) > todayMs).length,
      completed: todos.filter((t) => t.status === 'completed').length,
    };
  }, [todos, todayMs]);

  const monthLabel = `${MONTHS[today.getMonth()]} — ${today.getFullYear()}`;
  const longDate = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // Clickable "needs attention" counters — each links to the page that resolves it.
  const attentionTiles = [
    { key: 'approvals', label: 'Approvals pending', value: ops.proposed, to: '/org-admin/approvals' },
    { key: 'overdueComp', label: 'Overdue compliance', value: ops.overdueCompliance.length, to: '/org-admin/compliance-calendar' },
    { key: 'notices', label: 'Open notices', value: ops.notices.open || 0, to: '/org-admin/notices' },
    { key: 'renewals', label: 'Renewals ≤ 30 days', value: ops.renewals, to: '/org-admin/compliance-calendar' },
  ];

  // Deadline feed: overdue first (most urgent), then upcoming; both already
  // ascending by due date. Capped so the panel stays glanceable.
  const daysLeft = (d) => Math.round((startOfDay(new Date(d)).getTime() - today.getTime()) / 864e5);
  const deadlineList = [...ops.overdueCompliance, ...ops.upcomingCompliance].slice(0, 8);
  const ctName = (i) => {
    const ct = i.enrollmentId && i.enrollmentId.complianceTypeId;
    return (ct && (ct.name || ct.code)) || 'Compliance';
  };

  // Editorial headline composed from the real numbers.
  const dueTodayN = dueBuckets[0].count;
  const overdueBeyondN = dueBuckets[4].count;

  // Donut segments (clockwise from top) computed from the four status counts.
  let acc = 0;
  const donutSegs = statusCounts
    .filter((s) => s.value > 0)
    .map((s) => {
      const p = summaryTotal > 0 ? (s.value / summaryTotal) * 100 : 0;
      const seg = { key: s.key, color: s.color, p, offset: 25 - acc };
      acc += p;
      return seg;
    });

  // 30-day tasks-created sparkline.
  const spark = useMemo(() => {
    const days = 30;
    const start = startOfDay(new Date());
    start.setDate(start.getDate() - (days - 1));
    const counts = new Array(days).fill(0);
    tasks.forEach((t) => {
      if (!t.createdAt) return;
      const idx = Math.round((startOfDay(new Date(t.createdAt)).getTime() - start.getTime()) / 864e5);
      if (idx >= 0 && idx < days) counts[idx] += 1;
    });
    const max = Math.max(1, ...counts);
    const W = 280;
    const H = 84;
    const pad = 5;
    // Per-point geometry + metadata, so the hover overlay can place a marker
    // and tooltip. leftPct/topPct are percentages of the box, which stay
    // correct even though the SVG stretches (preserveAspectRatio="none").
    const points = counts.map((c, i) => {
      const x = (i / (days - 1)) * W;
      const y = H - pad - (c / max) * (H - 2 * pad);
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return { count: c, date: d, leftPct: (x / W) * 100, topPct: (y / H) * 100, xy: `${x.toFixed(1)},${y.toFixed(1)}` };
    });
    const pts = points.map((p) => p.xy);
    return { W, H, points, line: pts.join(' '), area: `0,${H} ${pts.join(' ')} ${W},${H}` };
  }, [tasks]);

  const axis = useMemo(() => {
    const s = new Date(today); s.setDate(today.getDate() - 29);
    const m = new Date(today); m.setDate(today.getDate() - 14);
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return [fmt(s), fmt(m), fmt(today)];
  }, [today]);

  if (SHOW_DASHBOARD_LOADER && loading) {
    return (
      <OrgAdminLayout title="Dashboard">
        <PageLoader />
      </OrgAdminLayout>
    );
  }

  return (
    <OrgAdminLayout title="Dashboard">
      <div className="dash2">
        {/* ---- Editorial hero ---- */}
        <div className="d2-hero">
          <div className="d2-kicker">{longDate}</div>
          <h1>
            {dueTodayN === 0 ? 'Nothing due today.' : `${dueTodayN} filing${dueTodayN === 1 ? '' : 's'} due today.`}{' '}
            {overdueBeyondN > 0 ? (
              <>
                <em>{overdueBeyondN} filing{overdueBeyondN === 1 ? '' : 's'}</em> {overdueBeyondN === 1 ? 'is' : 'are'} more than a week past deadline.
              </>
            ) : (
              'Nothing is badly overdue.'
            )}
          </h1>
        </div>

        {/* ---- Deadline stat row ---- */}
        {/* Each tile opens the task list filtered to exactly the window it
            counts. A tile showing ZERO stays a plain div: a button that opens
            an empty list is a dead end, and one that looks clickable but has
            nothing to show reads as broken. */}
        <div className="d2-deadline">
          {dueBuckets.map((b, idx) => {
            const tone = `d2-v ${DL_TONE[idx]}`;
            const cls = `d2-dl ${b.key === 'overdueBeyond' && b.count > 0 ? 'alert' : ''}`;
            const inner = (
              <>
                <div className={tone}>{b.count}</div>
                <div className="d2-l"><b>{b.main}</b>{b.sub}</div>
              </>
            );
            return b.count > 0 ? (
              <button
                key={b.key}
                type="button"
                className={`${cls} is-link`}
                onClick={() => navigate(`/org-admin/tasks?due=${b.due}`)}
                title={`Show the ${b.count} task${b.count === 1 ? '' : 's'} ${b.main.toLowerCase()} ${b.sub.toLowerCase()}`}
              >
                {inner}
              </button>
            ) : (
              <div key={b.key} className={cls}>{inner}</div>
            );
          })}
        </div>

        {/* ---- Three columns ---- */}
        <div className="d2-cols">
          {/* Left: To-Do + Needs attention */}
          <div className="d2-left">
            <section className="d2-sec">
              <div className="d2-sechead">
                <h2>To-Do</h2>
                <button type="button" className="d2-add" title="Open the To-Do page" onClick={() => navigate('/org-admin/todos')}><PlusIcon /></button>
              </div>
              <div className="d2-card">
                <button type="button" className="d2-big" onClick={() => navigate('/org-admin/todos')}>
                  <span className="d2-big-n">{todoStats.open}</span>
                  <span><span className="d2-big-t">Open items</span><span className="d2-big-s">Across the firm</span></span>
                </button>
                <div className="d2-quad">
                  {[
                    { n: todoStats.overdue, k: 'Overdue', c: 'c-clay' },
                    { n: todoStats.dueToday, k: 'Due today', c: 'c-gold' },
                    { n: todoStats.upcoming, k: 'Upcoming', c: 'c-deep' },
                    { n: todoStats.completed, k: 'Completed', c: 'c-sage' },
                  ].map((q) => (
                    <button key={q.k} type="button" className="d2-qi" onClick={() => navigate('/org-admin/todos')}>
                      <span className={`d2-qn ${q.c}`}>{q.n}</span>
                      <span className="d2-qk">{q.k}</span>
                    </button>
                  ))}
                </div>
              </div>
            </section>

            <section className="d2-sec">
              <div className="d2-sechead"><h2>Needs attention</h2></div>
              <div className="d2-card">
                <div className="d2-quad">
                  {attentionTiles.map((t) => (
                    <button key={t.key} type="button" className="d2-qi" onClick={() => navigate(t.to)}>
                      <span className={`d2-qn ${ATTN_TONE[t.key]}`}>{t.value}</span>
                      <span className="d2-qk">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          </div>

          {/* Middle: Tasks */}
          <section className="d2-sec">
            <div className="d2-sechead">
              <h2>Tasks</h2>
              <span className="d2-tasks-actions">
                <span className="d2-monthpill"><CalendarIcon /><span>{monthLabel}</span><ChevronDown /></span>
                <button type="button" className="d2-add" title="Create a task" onClick={() => navigate('/org-admin/tasks/create')}><PlusIcon /></button>
                <button type="button" className="d2-iconbtn" title="Open the Tasks page" onClick={() => navigate('/org-admin/tasks')}><KebabIcon /></button>
              </span>
            </div>
            <div className="d2-card">
              <div className="d2-tabs">
                {taskTabs.map((tab) => (
                  <button key={tab.key} type="button" className={activeTaskTab === tab.key ? 'on' : ''} onClick={() => setActiveTaskTab(tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>
              {groupedRows.length === 0 ? (
                <div className="d2-empty">No tasks in this status</div>
              ) : (
                groupedRows.map((g) => (
                  <div key={g.name} className="d2-row">
                    <span className="d2-chev" aria-hidden="true">›</span>
                    <span className="d2-name">{g.name}</span>
                    <span className="d2-ct">{g.active}</span>
                    <span className="d2-bar"><i style={{ width: `${Math.max(6, (g.active / maxActive) * 100)}%` }} /></span>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Right: Task summary + Performance */}
          <div className="d2-right">
            <section className="d2-sec">
              <div className="d2-sechead"><h2>Task summary</h2></div>
              <div className="d2-card">
                <svg className="d2-donut" width="140" height="140" viewBox="0 0 42 42" role="img" aria-label={`${needToComplete} tasks to complete`}>
                  <circle cx="21" cy="21" r="15.9" fill="none" stroke="#EEECE8" strokeWidth="4.5" />
                  {donutSegs.map((s) => (
                    <circle key={s.key} cx="21" cy="21" r="15.9" fill="none" stroke={s.color} strokeWidth="4.5" strokeLinecap="round"
                      strokeDasharray={`${s.p} ${100 - s.p}`} strokeDashoffset={s.offset} />
                  ))}
                  <text x="21" y="20.6" textAnchor="middle" fontSize="9" fontFamily="Fraunces, serif" fontWeight="600" fill="#1C1B19">{needToComplete}</text>
                  <text x="21" y="25.8" textAnchor="middle" fontSize="2.8" letterSpacing="0.3" fill="#A5A19A">OPEN</text>
                </svg>
                <ul className="d2-legend">
                  {statusCounts.map((s) => (
                    <li key={s.key}>
                      <button
                        type="button"
                        className="d2-legend-row"
                        title={`View ${s.label} tasks`}
                        onClick={() => navigate(`/org-admin/tasks?status=${s.key}`)}
                      >
                        <span><i className="d2-dot" style={{ background: s.color }} />{s.label}</span>
                        <b>{s.value}</b>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="d2-need"><span>Need to complete tasks</span><b>{needToComplete}</b></div>
              </div>
            </section>

            <section className="d2-sec">
              <div className="d2-sechead"><h2>Performance</h2></div>
              <PerformanceChart spark={spark} axis={axis} />
            </section>
          </div>
        </div>

        {/* ---- Upcoming statutory deadlines (full width) ---- */}
        <section className="d2-sec">
          <div className="d2-sechead">
            <h2>Upcoming statutory deadlines</h2>
            <button type="button" className="d2-link" onClick={() => navigate('/org-admin/compliance-calendar')}>View calendar →</button>
          </div>
          <div className="d2-card">
            {deadlineList.length === 0 ? (
              <div className="d2-empty">
                <div>No dated filings due soon.</div>
                <div className="d2-empty-sub">Statutory due dates are set on the compliance calendar.</div>
              </div>
            ) : (
              deadlineList.map((i) => {
                const n = daysLeft(i.statutoryDueDate);
                const badge = n < 0 ? { c: 'b-bad', t: `${Math.abs(n)}d overdue` } : n <= 7 ? { c: 'b-warn', t: 'Due soon' } : { c: 'b-ok', t: 'On track' };
                return (
                  <button key={i._id} type="button" className="d2-dlitem" onClick={() => navigate('/org-admin/compliance-calendar')}>
                    <div className="d2-dlmain">
                      <span className="d2-dlname">{(i.clientId && i.clientId.displayName) || '—'}</span>
                      <span className="d2-dlsub">{ctName(i)}{i.periodLabel ? ` · ${i.periodLabel}` : ''}</span>
                    </div>
                    <span className="d2-dldate">{new Date(i.statutoryDueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    <span className={`d2-badge ${badge.c}`}>{badge.t}</span>
                  </button>
                );
              })
            )}
          </div>
        </section>
      </div>
    </OrgAdminLayout>
  );
}

// The 30-day "tasks created" sparkline, now hoverable: moving across it snaps
// to the nearest day and shows a marker + tooltip with that day's count.
function PerformanceChart({ spark, axis }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const rel = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(rel * (spark.points.length - 1));
    setHoverIdx(Math.max(0, Math.min(spark.points.length - 1, idx)));
  };

  const hp = hoverIdx != null ? spark.points[hoverIdx] : null;
  // Keep the tooltip inside the box at the two edges.
  const tipTx = hp ? (hp.leftPct > 78 ? '-92%' : hp.leftPct < 22 ? '-8%' : '-50%') : '-50%';

  return (
    <div className="d2-card">
      <div
        className="d2-spark-wrap"
        ref={wrapRef}
        onMouseMove={onMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <svg className="d2-spark" viewBox={`0 0 ${spark.W} ${spark.H}`} preserveAspectRatio="none" role="img" aria-label="Tasks created, last 30 days">
          <polygon points={spark.area} fill="#EFF2F0" />
          <polyline points={spark.line} fill="none" stroke="#4F6F5C" strokeWidth="2" strokeLinejoin="round" />
        </svg>
        {hp && (
          <>
            <div className="d2-spark-guide" style={{ left: `${hp.leftPct}%` }} />
            <div className="d2-spark-dot" style={{ left: `${hp.leftPct}%`, top: `${hp.topPct}%` }} />
            <div
              className="d2-spark-tip"
              style={{ left: `${hp.leftPct}%`, top: `${hp.topPct}%`, transform: `translate(${tipTx}, calc(-100% - 12px))` }}
            >
              <span className="d2-spark-tip-count">{hp.count} task{hp.count === 1 ? '' : 's'}</span>
              <span className="d2-spark-tip-date">
                {hp.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
              </span>
            </div>
          </>
        )}
      </div>
      <div className="d2-axis"><span>{axis[0]}</span><span>{axis[1]}</span><span>{axis[2]}</span></div>
    </div>
  );
}

export default OrgAdminDashboard;
